import React, { createContext, useState, useContext, useEffect, useRef, useCallback, useMemo, ReactNode } from "react";
import { getDocs, query, where, updateDoc, DocumentReference } from "firebase/firestore";
import { auth, dbUsers, onAuthStateChanged } from "../firebase";
import { UserSettings, UserSettingsContextValue } from "../types";

const UserSettingsContext = createContext<UserSettingsContextValue | null>(null);

interface UserSettingsProviderProps {
  children: ReactNode;
}

/** Fields that sync to Firestore (cross-device). The rest stay localStorage-only. */
const SYNCED_KEYS: (keyof UserSettings)[] = ["hiddenExercises", "weightUnit", "defaultRestTimer", "notifications", "notificationPrefs", "activityTracking", "profilePhoto", "fitnessLevel", "proteinTarget", "proteinGoal", "dailyCalorieIntakeOverride", "calorieGoal", "activityLevel", "biologicalSex", "heightCm", "ageYears", "pillReminder", "contraceptionTracking", "navBarItems"];

export const UserSettingsProvider: React.FC<UserSettingsProviderProps> = ({ children }) => {
  const defaultSettings: UserSettings = {
    theme: "light",
    useIcons: false,
    notifications: true,
    notificationPrefs: {
      tasks: true,
      workouts: true,
      social: true,
      announcements: true,
      engagement: true,
      cycle: true,
      pill: true,
      emails: true,
    },
    navBarItems: ["notes", "tasks", "calendar", "community"],
  };

  const [settings, setSettings] = useState<UserSettings>(() => {
    const storedSettings = localStorage.getItem("userSettings");
    return storedSettings ? JSON.parse(storedSettings) : defaultSettings;
  });

  const firestoreWriteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const uidRef = useRef<string | null>(null);
  const userDocRef = useRef<DocumentReference | null>(null);

  // Persist to localStorage on every change
  useEffect(() => {
    localStorage.setItem("userSettings", JSON.stringify(settings));
  }, [settings]);

  // Load synced fields from Firestore when user logs in
  // User docs are created with addDoc (random ID) and have a `userId` field,
  // so we must query by userId field — NOT use uid as the document ID.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      uidRef.current = user?.uid ?? null;
      userDocRef.current = null;
      if (!user) return;
      try {
        // Find the user doc by userId field (addDoc creates random IDs)
        const snap = await getDocs(query(dbUsers, where("userId", "==", user.uid)));
        if (!snap.empty) {
          userDocRef.current = snap.docs[0].ref;
          const remote = snap.docs[0].data()?.settings as Partial<UserSettings> | undefined;
          if (remote) {
            setSettings((prev) => {
              const merged = { ...prev };
              for (const key of SYNCED_KEYS) {
                if (remote[key] !== undefined) {
                  (merged as any)[key] = remote[key];
                }
              }
              return merged;
            });
          } else {
            // New account with no Firestore settings — reset to defaults so
            // stale localStorage from a previous account doesn't carry over.
            // Only reset synced keys so that in-flight local changes (e.g. a
            // just-completed onboarding skip that updated navBarItems before
            // this async listener resolved) aren't clobbered by the reset.
            setSettings((prev) => {
              const next = { ...prev };
              for (const key of SYNCED_KEYS) {
                (next as any)[key] = (defaultSettings as any)[key];
              }
              return next;
            });
          }
        }
      } catch {
        // Firestore unavailable — fall back to localStorage silently
      }
    });
    return () => unsub();
  }, []);

  // Debounced write of synced fields to Firestore
  const syncToFirestore = useCallback((updated: UserSettings) => {
    if (!userDocRef.current) return;
    if (firestoreWriteTimer.current) clearTimeout(firestoreWriteTimer.current);
    firestoreWriteTimer.current = setTimeout(async () => {
      if (!userDocRef.current) return;
      try {
        const payload: Record<string, any> = {};
        for (const key of SYNCED_KEYS) {
          if (updated[key] !== undefined) {
            payload[`settings.${key}`] = updated[key];
          }
        }
        if (Object.keys(payload).length > 0) {
          await updateDoc(userDocRef.current, payload);
        }
      } catch {
        // Firestore write failed — localStorage still has the data
      }
    }, 500);
  }, []);

  const updateSettings = useCallback((newSettings: Partial<UserSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...newSettings };
      // Only sync to Firestore if a synced field changed
      const hasSyncedChange = SYNCED_KEYS.some((k) => k in newSettings);
      if (hasSyncedChange) syncToFirestore(next);
      return next;
    });
  }, [syncToFirestore]);

  const contextValue = useMemo(() => ({
    settings, updateSettings,
  }), [settings, updateSettings]);

  return (
    <UserSettingsContext.Provider value={contextValue}>
      {children}
    </UserSettingsContext.Provider>
  );
};

export const useUserSettings = (): UserSettingsContextValue => {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error("useUserSettings must be used within a UserSettingsProvider");
  }
  return context;
};
